import React, { Suspense } from "react";

import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import type { TranslateControlProps } from "@/main/translation/TranslateControl";

/**
 * The lazy boundary for the translate control (rule 26: never a bare `import()`; rule 27: the control,
 * its dialog, its popover and the HTML walker stay off the startup path).
 *
 * <p>Keep this file TINY: every host that can offer a translation imports it eagerly. Hosts render it
 * only after `hasTranslatorBackend()` said a translator exists, so a browser with none never fetches
 * the chunk.
 *
 * <p>`null` for BOTH fallbacks, deliberately: the control is purely additive. The server renders no
 * markup for it, so a pending or failed chunk leaves the page exactly as the server sent it (rule 32's
 * fourth door does not apply to a slot with no SSR twin).
 */
const TranslateControlImpl = lazyWithRetry<React.ComponentType<TranslateControlProps>>(
    () => import("@/main/translation/TranslateControl").then((module) => ({ default: module.default })),
    "translate-control",
);

export default function TranslateControlLazy(props: TranslateControlProps) {
    return (
        <LazyControlBoundary controlName="translate-control" fallback={null}>
            <Suspense fallback={null}>
                <TranslateControlImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
